import React, { useEffect, useRef, useCallback, useState } from 'react';
import { AgentView, ConversationView, EmptyStateView, ChatSkeleton, ChatTopbar, ConversationHistoryView } from './components';
import { useChatAttachments, useChatSlideOutResize, useSlideOutDocuments } from './hooks';
import { SlideOutContainer, ChatPanelWrapper, DragHandle } from './ChatSlideOut.styled';
import { useHomeChat } from '../../context';
import { getDefaultAgentForNewChat } from '../../utils/agentUtils';
import DocumentPanel from '../DocumentPanel';
import SaveToDriveModal from '../SaveToDriveModal/SaveToDriveModal';
import DeleteDocumentModal from '../DocumentPanel/DeleteDocumentModal';
import DrivePickerModal from '../DrivePickerModal';

const ADD_FROM_DRIVE_VALUES = ['drive', 'addFromDrive'];

const ChatSlideOutInner = ({ onClose, onOpenInHome, isOpen = true }) => {
  const {
    agents,
    isLoadingAgents,
    selectedAgentId,
    selectedConversationId,
    messages,
    isLoadingMessages,
    fetchMessages,
    createNewConversation,
    selectAgent,
    defaultAgentIdForNewChat,
    defaultAgentForNewChat,
    setIsSlideOutMode,
    isDocumentPanelOpen,
    closeDocumentPanel,
  } = useHomeChat();

  const [isHistoryView, setIsHistoryView] = useState(false);
  const [showDrivePickerModal, setShowDrivePickerModal] = useState(false);

  // Only signal slide-out mode when the panel is actually visible.
  // When kept in DOM but hidden (isOpen=false), we must not affect the shared
  // context so that other panels (e.g. Home's ChatPanel) behave correctly.
  useEffect(() => {
    if (!isOpen) return;
    setIsSlideOutMode(true);
    return () => setIsSlideOutMode(false);
  }, [setIsSlideOutMode, isOpen]);

  const handleViewHistory = useCallback(() => setIsHistoryView(true), []);
  const handleBackFromHistory = useCallback(() => setIsHistoryView(false), []);

  // Preselect default agent when opening slide-out with no active conversation.
  // Only runs when no agent is selected yet — does NOT override user's explicit selection.
  // TODO(COPILOT): When backend supports synthetic Copilot UUID, use defaultAgentForNewChat for scope.
  useEffect(() => {
    if (!isOpen || isLoadingAgents) return;
    if (selectedConversationId) return;
    if (selectedAgentId) return; // User has already selected an agent — respect that
    const isCopilot = defaultAgentForNewChat?.isCopilot;
    const fallback = isCopilot && agents?.length ? getDefaultAgentForNewChat(agents) : null;
    const agentToSelect = fallback?.agent ?? defaultAgentForNewChat;
    const agentIdToSelect = fallback?.id ?? (agentToSelect ? (agentToSelect.uuid || agentToSelect.id) : defaultAgentIdForNewChat);
    if (!agentIdToSelect || !agentToSelect) return;
    if (!isCopilot && !agents?.length) return;
    selectAgent(agentIdToSelect, agentToSelect);
  }, [
    isOpen,
    agents?.length,
    isLoadingAgents,
    selectedAgentId,
    selectedConversationId,
    defaultAgentIdForNewChat,
    defaultAgentForNewChat,
    selectAgent,
  ]);

  useEffect(() => {
    if (selectedConversationId) {
      fetchMessages(selectedConversationId);
    }
  }, [selectedConversationId, fetchMessages]);

  const {
    attachments,
    handleFilesSelected,
    handleAttachmentRemove,
    handleAddDocumentsFromDrive,
    clearAttachments,
    handleAddMenuChange,
    handleModeMenuChange,
  } = useChatAttachments({
    agentUuid: selectedAgentId,
    conversationUuid: selectedConversationId,
    createNewConversation,
  });

  const {
    commonDocProps,
    showSaveToDriveModal,
    docToSave,
    isSaving,
    handleCloseSaveToDriveModal,
    handleSaveToDrive,
    documentToDelete,
    handleCloseDeleteModal,
    handleConfirmDeleteDocument,
  } = useSlideOutDocuments();

  const handleModeTagClose = useCallback(() => {
    clearAttachments();
  }, [clearAttachments]);

  const handleAddMenuChangeWithDrive = useCallback(
    (value, itemData) => {
      if (ADD_FROM_DRIVE_VALUES.includes(value)) {
        setShowDrivePickerModal(true);
      } else {
        handleAddMenuChange?.(value, itemData);
      }
    },
    [handleAddMenuChange]
  );

  const handleCloseDrivePickerModal = useCallback(
    () => setShowDrivePickerModal(false),
    []
  );

  const handleDrivePickerConfirm = useCallback(
    (selectedDocuments) => {
      if (selectedDocuments?.length) {
        handleAddDocumentsFromDrive(selectedDocuments);
      }
      setShowDrivePickerModal(false);
    },
    [handleAddDocumentsFromDrive]
  );

  const showEmptyState = !selectedConversationId && !selectedAgentId;
  const showConversationSkeleton = selectedConversationId && isLoadingMessages;
  const showConversationView = selectedConversationId && !isLoadingMessages && messages.length > 0;
  const showConversationEmpty = selectedConversationId && !isLoadingMessages && messages.length === 0;
  const showAgentView = selectedAgentId && !selectedConversationId;

  let content = null;
  if (showEmptyState) {
    content = (
      <EmptyStateView
        attachments={attachments}
        onFilesSelected={handleFilesSelected}
        onAttachmentRemove={handleAttachmentRemove}
        onClearAttachments={clearAttachments}
        onAddMenuChange={handleAddMenuChangeWithDrive}
        onModeMenuChange={handleModeMenuChange}
        onModeTagClose={handleModeTagClose}
        modelAlignMenu="left"
        {...commonDocProps}
      />
    );
  } else if (showConversationSkeleton) {
    content = <ChatSkeleton show />;
  } else if (showConversationView) {
    content = (
      <ConversationView
        messages={messages}
        attachments={attachments}
        onFilesSelected={handleFilesSelected}
        onAttachmentRemove={handleAttachmentRemove}
        onClearAttachments={clearAttachments}
        onAddMenuChange={handleAddMenuChangeWithDrive}
        onModeTagClose={handleModeTagClose}
        onModeMenuChange={handleModeMenuChange}
        clearAttachments={clearAttachments}
        isLoadingMessages={isLoadingMessages}
        {...commonDocProps}
      />
    );
  } else if (showAgentView || showConversationEmpty) {
    content = (
      <AgentView
        selectedAgentId={selectedAgentId}
        attachments={attachments}
        onFilesSelected={handleFilesSelected}
        onAttachmentRemove={handleAttachmentRemove}
        onClearAttachments={clearAttachments}
        onAddMenuChange={handleAddMenuChangeWithDrive}
        onModeTagClose={handleModeTagClose}
        onModeMenuChange={handleModeMenuChange}
        clearAttachments={clearAttachments}
        isLoadingMessages={isLoadingMessages}
        showModelSelector
        modelAlignMenu="left"
        {...commonDocProps}
      />
    );
  }

  if (isHistoryView) {
    return <ConversationHistoryView onBack={handleBackFromHistory} />;
  }

  if (isDocumentPanelOpen) {
    return (
      <>
        <DocumentPanel onBack={closeDocumentPanel} />
        <SaveToDriveModal
          isOpen={showSaveToDriveModal}
          onClose={handleCloseSaveToDriveModal}
          documentTitle={docToSave?.title ?? 'Document'}
          onSave={handleSaveToDrive}
        />
        <DeleteDocumentModal
          isOpen={!!documentToDelete}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDeleteDocument}
          documentTitle={documentToDelete?.title ?? ''}
        />
      </>
    );
  }

  return (
    <>
      <ChatTopbar onClose={onClose} onViewHistory={handleViewHistory} onOpenInHome={onOpenInHome} />
      <ChatPanelWrapper>
        {content}
      </ChatPanelWrapper>
      <SaveToDriveModal
        isOpen={showSaveToDriveModal}
        onClose={handleCloseSaveToDriveModal}
        documentTitle={docToSave?.title ?? 'Document'}
        onSave={handleSaveToDrive}
      />
      <DeleteDocumentModal
        isOpen={!!documentToDelete}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDeleteDocument}
        documentTitle={documentToDelete?.title ?? ''}
      />
      <DrivePickerModal
        isOpen={showDrivePickerModal}
        onClose={handleCloseDrivePickerModal}
        onConfirm={handleDrivePickerConfirm}
      />
    </>
  );
};

const ChatSlideOut = ({ onClose, onOpenInHome, isOpen = true }) => {
  const { width, handleMouseDown, containerRef } = useChatSlideOutResize();
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = useCallback((e) => {
    setIsDragging(true);
    handleMouseDown(e);
  }, [handleMouseDown]);

  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  return (
    <SlideOutContainer ref={containerRef} $width={width}>
      <DragHandle
        onMouseDown={handleDragStart}
        data-dragging={isDragging ? 'true' : undefined}
        aria-hidden
      />
      <ChatSlideOutInner onClose={onClose} onOpenInHome={onOpenInHome} isOpen={isOpen} />
    </SlideOutContainer>
  );
};

export default ChatSlideOut;
